import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { startOfWeek, addDays, setHours, setMinutes } from "date-fns"
import type React from "react"
import { useMemo, useState } from "react"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import CalendarView, {
  type CalendarEvent,
  SWIM_LEVEL_COLORS,
} from "@/components/CalendarView"

import { ClassTemplateActionsMenu } from "@/components/layout/Common/ClassTemplateActionsMenu"
import { useSchoolSettings } from "@/hooks/api/useSchoolSettings"
import { ClassTemplateCard } from "@/components/molecules/ClassTemplateCard"
import { type ViewMode, ViewToggle } from "@/components/molecules/ViewToggle"
import {
  DataTable,
  type DataTableColumn,
} from "@/components/organisms/DataTable"
import { AddClassTemplate } from "@/features/classTemplates"
import { useSessions } from "@/hooks/api/useSessions"
import { usePermissions } from "@/hooks/usePermissions"
import { useIsMobile } from "@/hooks/useResponsive"
import { api } from "@/services/api"
import type {
  ClassTemplateListResponse,
  ClassTemplateResponse,
} from "@/types/class_template"
import { unwrapStandardResponse } from "@/utils/apiResponse"

const classTemplatesSearchSchema = z.object({
  page: z.coerce.number().min(1).catch(1),
  search: z.string().optional(),
  sort: z.enum(["created_at", "updated_at"]).catch("created_at"),
  direction: z.enum(["asc", "desc"]).catch("desc"),
  swim_level: z.string().optional(),
  day_of_week: z.string().optional(),
  session_id: z.string().optional(),
  time_of_day: z.string().optional(),
})

const PER_PAGE = 25
type SortField = "created_at" | "updated_at"
type SortDirection = "asc" | "desc"

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

function getClassTemplatesQueryOptions({
  page,
  search,
  sort,
  direction,
  swimLevel,
  dayOfWeek,
  sessionId,
  timeOfDay,
}: {
  page: number
  search?: string
  sort: SortField
  direction: SortDirection
  swimLevel?: string
  dayOfWeek?: string
  sessionId?: string
  timeOfDay?: string
}) {
  const debugClassTemplates =
    import.meta.env.DEV && import.meta.env.VITE_DEBUG_CLASSTEMPLATES === "true"

  return {
    queryFn: async () => {
      if (debugClassTemplates) {
        console.log(
          `ClassTemplates page: Fetching page ${page} with ${PER_PAGE} records per page`,
        )
      }

      const response =
        await api.classTemplates.readClassTemplatesApiV1ClassTemplatesGet({
          skip: (page - 1) * PER_PAGE,
          limit: PER_PAGE,
          search,
          sortBy: sort,
          sortOrder: direction,
          swimLevel,
          dayOfWeek,
          sessionId,
          timeOfDay,
        })

      const payload =
        unwrapStandardResponse<ClassTemplateListResponse>(response)
      const items = payload?.items ?? []
      const total = payload?.total ?? 0

      if (debugClassTemplates) {
        console.log("ClassTemplates page: API payload:", payload)
        console.log("ClassTemplates page: items count:", items.length)
        console.log("ClassTemplates page: total:", total)
      }

      return {
        data: items,
        count: total,
      }
    },
    queryKey: [
      "classTemplates",
      {
        page,
        search,
        sort,
        direction,
        swimLevel,
        dayOfWeek,
        sessionId,
        timeOfDay,
      },
    ],
  }
}

export const Route = createFileRoute("/_layout/classTemplates")({
  component: ClassTemplates,
  validateSearch: (search) => classTemplatesSearchSchema.parse(search),
})

const selectStyle = {
  borderRadius: "md",
  borderWidth: "1px",
  borderColor: "gray.200",
  px: 3,
  py: 1,
  fontSize: "sm",
  bg: "white",
  _hover: { borderColor: "gray.300" },
  _focus: {
    borderColor: "blue.500",
    outline: "none",
    boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
  },
}

function ClassTemplates() {
  const navigate = useNavigate({ from: Route.fullPath })
  const {
    page,
    search,
    sort,
    direction,
    swim_level,
    day_of_week,
    session_id,
    time_of_day,
  } = Route.useSearch()
  const { hasPermission } = usePermissions()
  const [searchInput, setSearchInput] = useState(search ?? "")
  const [viewMode, setViewMode] = useState<ViewMode>("table")
  const isMobile = useIsMobile()
  const effectiveViewMode = isMobile ? "cards" : viewMode
  const calendarModes: ViewMode[] = ["table", "cards", "calendar"]
  const { swimLevels } = useSchoolSettings()

  const { data: sessionsData } = useSessions({ limit: 100 })
  const sessions = sessionsData?.items ?? []

  const hasActiveFilters = !!(swim_level || day_of_week || session_id || time_of_day)

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getClassTemplatesQueryOptions({
      page,
      search: search || undefined,
      sort,
      direction,
      swimLevel: swim_level,
      dayOfWeek: day_of_week,
      sessionId: session_id,
      timeOfDay: time_of_day,
    }),
    placeholderData: (previous) => previous,
  })

  const setPage = (nextPage: number) =>
    navigate({
      search: (previous) => ({
        ...previous,
        page: nextPage,
      }),
    })

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextValue = searchInput.trim()
    navigate({
      search: (previous) => {
        const { search: _prevSearch, ...rest } = previous
        return nextValue
          ? { ...rest, page: 1, search: nextValue }
          : { ...rest, page: 1 }
      },
    })
  }

  const handleSortChange = (nextSort: SortField) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        sort: nextSort,
      }),
    })
  }

  const handleDirectionChange = (nextDirection: SortDirection) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        direction: nextDirection,
      }),
    })
  }

  const handleFilterChange = (key: string, value: string) => {
    navigate({
      search: (previous) => {
        const next: Record<string, unknown> = { ...previous, page: 1 }
        if (value) {
          next[key] = value
        } else {
          delete next[key]
        }
        return next as typeof previous
      },
    })
  }

  const clearFilters = () => {
    navigate({
      search: (previous) => {
        const {
          swim_level: _,
          day_of_week: __,
          session_id: ___,
          time_of_day: ____,
          ...rest
        } = previous
        return { ...rest, page: 1 }
      },
    })
  }

  const records = data?.data?.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const showActions =
    hasPermission("class_templates:update") ||
    hasPermission("class_templates:delete")

  const columns = useMemo<DataTableColumn<ClassTemplateResponse>[]>(() => {
    const tableColumns: DataTableColumn<ClassTemplateResponse>[] = []

    tableColumns.push({
      key: "name",
      label: "Class Name",
      render: (classTemplate) => (
        <Text fontWeight="medium">
          {formatFieldValue(classTemplate.name, "str")}
        </Text>
      ),
    })

    tableColumns.push({
      key: "instructor_name",
      label: "Instructor",
      render: (classTemplate) => (
        <Text>{classTemplate.instructor_name || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "swim_level",
      label: "Swim Level",
      render: (classTemplate) => (
        <Text>{formatFieldValue(classTemplate.swim_level, "str")}</Text>
      ),
    })

    tableColumns.push({
      key: "schedule",
      label: "Schedule",
      render: (classTemplate) => (
        <Text>
          {classTemplate.day_of_week} {classTemplate.start_time}-
          {classTemplate.end_time}
        </Text>
      ),
    })

    tableColumns.push({
      key: "location",
      label: "Location",
      render: (classTemplate) => (
        <Text>{formatFieldValue(classTemplate.location, "str")}</Text>
      ),
    })

    tableColumns.push({
      key: "capacity",
      label: "Capacity",
      render: (classTemplate) => (
        <Text>
          {classTemplate.current_enrollment || 0}/{classTemplate.max_students}
        </Text>
      ),
    })

    tableColumns.push({
      key: "price",
      label: "Price",
      render: (classTemplate) => (
        <Text>${formatFieldValue(classTemplate.price, "float")}</Text>
      ),
    })

    tableColumns.push({
      key: "status",
      label: "Status",
      render: (classTemplate) => {
        const isActive = classTemplate.is_active !== false
        const isPublished = classTemplate.is_published !== false
        const status = !isActive
          ? "Inactive"
          : !isPublished
            ? "Draft"
            : "Published"
        const colorScheme = !isActive
          ? "gray"
          : !isPublished
            ? "orange"
            : "green"

        return (
          <Box
            as="span"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            bg={`${colorScheme}.50`}
            color={`${colorScheme}.700`}
          >
            {status}
          </Box>
        )
      },
    })

    if (showActions) {
      tableColumns.push({
        key: "actions",
        label: "Actions",
        render: (classTemplate) => (
          <ClassTemplateActionsMenu classTemplate={classTemplate} />
        ),
      })
    }

    return tableColumns
  }, [showActions])

  // Generate calendar events from class templates
  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const dayMap: Record<string, number> = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    }

    const parseTime = (timeStr: string): { hours: number; minutes: number } | null => {
      if (!timeStr) return null
      // Handle "4:00 PM", "10:30 AM", "16:00", etc.
      const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i)
      if (!match) return null
      let hours = parseInt(match[1], 10)
      const minutes = parseInt(match[2], 10)
      const period = match[3]?.toUpperCase()
      if (period === "PM" && hours < 12) hours += 12
      if (period === "AM" && hours === 12) hours = 0
      return { hours, minutes }
    }

    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 })

    return records
      .map((ct: ClassTemplateResponse) => {
        const dayIndex = dayMap[ct.day_of_week]
        if (dayIndex === undefined) return null

        const startParsed = parseTime(ct.start_time)
        if (!startParsed) return null

        const eventDate = addDays(weekStart, dayIndex)
        const start = setMinutes(setHours(eventDate, startParsed.hours), startParsed.minutes)

        let end: Date
        if (ct.end_time) {
          const endParsed = parseTime(ct.end_time)
          if (endParsed) {
            end = setMinutes(setHours(eventDate, endParsed.hours), endParsed.minutes)
          } else {
            end = new Date(start.getTime() + (ct.duration_minutes || 30) * 60000)
          }
        } else {
          end = new Date(start.getTime() + (ct.duration_minutes || 30) * 60000)
        }

        return {
          id: ct.id,
          title: `${ct.name} (${ct.current_enrollment || 0}/${ct.max_students})`,
          start,
          end,
          color: SWIM_LEVEL_COLORS[ct.swim_level] || "#BEE3F8",
        } as CalendarEvent
      })
      .filter(Boolean) as CalendarEvent[]
  }, [records])

  if (isLoading) {
    return (
      <Container maxW="full">
        <Flex justifyContent="space-between" alignItems="center" pt={12} mb={6}>
          <Heading size="lg">Classes</Heading>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={4}
          minH="240px"
        >
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading classes...</Text>
        </Flex>
      </Container>
    )
  }

  return (
    <Container maxW="full">
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        pt={12}
        mb={6}
        gap={{ base: 3, md: 0 }}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg">Classes</Heading>
          <Box display={{ base: "block", md: "none" }}>
            <AddClassTemplate />
          </Box>
        </Flex>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 4 }}
          align={{ base: "stretch", md: "center" }}
        >
          <form onSubmit={handleSearchSubmit}>
            <HStack gap={2}>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search classes"
                size="sm"
                width={{ base: "full", md: "220px" }}
                aria-label="Search classes"
              />
              <Button type="submit" size="sm" colorPalette="blue">
                Search
              </Button>
            </HStack>
          </form>
          <HStack gap={2}>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={sort}
              onChange={(event) =>
                handleSortChange(
                  (event.target as HTMLSelectElement).value as SortField,
                )
              }
              aria-label="Sort classes"
              width={{ base: "full", md: "170px" }}
              flex={{ base: 1, md: "none" }}
              {...selectStyle}
            >
              <option value="created_at">Newest</option>
              <option value="updated_at">Recently updated</option>
              <option value="name">Name</option>
            </Box>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={direction}
              onChange={(event) =>
                handleDirectionChange(
                  (event.target as HTMLSelectElement).value as SortDirection,
                )
              }
              aria-label="Sort direction"
              width={{ base: "full", md: "150px" }}
              flex={{ base: 1, md: "none" }}
              {...selectStyle}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </Box>
          </HStack>
          <Box display={{ base: "none", md: "flex" }}>
            <HStack gap={4}>
              <ViewToggle value={viewMode} onChange={setViewMode} modes={calendarModes} />
              <AddClassTemplate />
            </HStack>
          </Box>
        </Flex>
      </Flex>

      {/* Filter dropdowns */}
      <Flex
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 3 }}
        mb={4}
        align={{ base: "stretch", md: "center" }}
      >
        <Box
          {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
          as="select"
          size="sm"
          value={swim_level ?? ""}
          onChange={(event) =>
            handleFilterChange(
              "swim_level",
              (event.target as HTMLSelectElement).value,
            )
          }
          aria-label="Filter by swim level"
          width={{ base: "full", md: "180px" }}
          {...selectStyle}
        >
          <option value="">All Swim Levels</option>
          {swimLevels.map((sl) => (
            <option key={sl.value} value={sl.value}>
              {sl.label}
            </option>
          ))}
        </Box>
        <Box
          {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
          as="select"
          size="sm"
          value={day_of_week ?? ""}
          onChange={(event) =>
            handleFilterChange(
              "day_of_week",
              (event.target as HTMLSelectElement).value,
            )
          }
          aria-label="Filter by day of week"
          width={{ base: "full", md: "160px" }}
          {...selectStyle}
        >
          <option value="">All Days</option>
          {DAYS_OF_WEEK.map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </Box>
        <Box
          {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
          as="select"
          size="sm"
          value={session_id ?? ""}
          onChange={(event) =>
            handleFilterChange(
              "session_id",
              (event.target as HTMLSelectElement).value,
            )
          }
          aria-label="Filter by season"
          width={{ base: "full", md: "200px" }}
          {...selectStyle}
        >
          <option value="">All Seasons</option>
          {sessions.map((session) => (
            <option key={session.id} value={session.id}>
              {session.name}
            </option>
          ))}
        </Box>
        <Box
          {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
          as="select"
          size="sm"
          value={time_of_day ?? ""}
          onChange={(event) =>
            handleFilterChange(
              "time_of_day",
              (event.target as HTMLSelectElement).value,
            )
          }
          aria-label="Filter by time of day"
          width={{ base: "full", md: "170px" }}
          {...selectStyle}
        >
          <option value="">All Times</option>
          <option value="morning">Morning (6am-12pm)</option>
          <option value="afternoon">Afternoon (12pm-6pm)</option>
          <option value="evening">Evening (6pm-6am)</option>
        </Box>
        {hasActiveFilters && (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </Flex>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <HStack gap={2} mb={4} flexWrap="wrap">
          {swim_level && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              Level: {swim_level}
            </Box>
          )}
          {day_of_week && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              Day: {day_of_week}
            </Box>
          )}
          {session_id && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              Season:{" "}
              {sessions.find((s) => s.id === session_id)?.name ??
                "Selected"}
            </Box>
          )}
          {time_of_day && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              Time:{" "}
              {time_of_day.charAt(0).toUpperCase() + time_of_day.slice(1)}
            </Box>
          )}
        </HStack>
      )}

      {records.length === 0 ? (
        <VStack gap={4} py={12} alignItems="center">
          <FiSearch size={48} color="gray" />
          <VStack textAlign="center" gap={2}>
            <Heading size="md" color="gray.600">
              {hasActiveFilters
                ? "No classes match your filters"
                : "You don't have any classes yet"}
            </Heading>
            <Text color="gray">
              {hasActiveFilters
                ? "Try adjusting your filters"
                : "Add a new class to get started"}
            </Text>
          </VStack>
        </VStack>
      ) : (
        <>
          <Box opacity={isPlaceholderData ? 0.5 : 1}>
            {effectiveViewMode === "calendar" ? (
              <CalendarView events={calendarEvents} />
            ) : effectiveViewMode === "cards" ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                {records.map((classTemplate: ClassTemplateResponse) => (
                  <ClassTemplateCard
                    key={classTemplate.id}
                    classTemplate={classTemplate}
                    showActions={showActions}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <DataTable columns={columns} data={records} size="sm" />
            )}
          </Box>
          <Flex
            justifyContent={{ base: "center", md: "flex-end" }}
            mt={4}
            gap={2}
          >
            <Button
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled>
              Page {page} of {Math.ceil(count / PER_PAGE)}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setPage(Math.min(Math.ceil(count / PER_PAGE), page + 1))
              }
              disabled={page >= Math.ceil(count / PER_PAGE)}
            >
              Next
            </Button>
          </Flex>
        </>
      )}
    </Container>
  )
}
